/**
 * Market making model (docs/market-making-ux.md) — pure, side-effect-free (apart from the
 * registry's injected storage), unit-tested in marketMaking.test.ts.
 *
 *   markets     the two-sided books a game offers: the 2-way moneyline, spreads and totals at
 *               each line, and two-way props (yes/no or over/under per participant + line)
 *   ladder      one book as venue rows × the two sides, each cell with its bid cap (best ask −
 *               1¢, clamped to 2–98¢), its maker fee, and why it's disabled when it is
 *   plan        Revision 1: a target margin (after fees) + contracts per side → the two bids,
 *               centred on the normalised consensus and clamped so they rest; the result per
 *               outcome (existing fills included); the multi-leg limit `buy` (explicit mode)
 *   book        an opportunity's legs → filled / quoted contracts per side, cost (fees when the
 *               rows carry them), payout by outcome, expected payout, and the status chip
 *   hedge       the flattening trade: buy the contract difference on the short side
 *   KPI         Revision 2: the one big number and its state (draft / working / exposed / locked /
 *               settled); "Get out now" (the locked result of buying the short side's missing
 *               contracts at the best live venue price, taker fee ≈ in), "Or wait" (if the short
 *               side's resting bids fill), and Working's "market moved away" + Move bid
 *   model       every Market making order is filed under one OpenMarkets model, "greenbook ·
 *               Market making" (docs: Models & attribution), so the list is the server's
 *               `GET /auth/opportunities?model_id=…` on any device; `entryFromOpportunity` rebuilds
 *               a card from an opportunity's legs + the game's books when this browser has no entry
 *   registry    a local cache + enrichment of the quotes placed here (localStorage): the venue each
 *               paper leg targeted, the labels as placed, and the legacy quotes from before models
 *               (no model, so only this browser knows them). `mergeQuotes` joins it with the server
 *               list; a local "hidden" set lets Remove drop a server-listed card
 *
 * Every price here is a whole number of cents inside 2–98¢: the API rejects 1¢ even though its
 * error says 1¢ is allowed (FRICTION.md).
 */
import { useMemo, useSyncExternalStore } from "react";
import type { BuyLeg, BuyResponse, MoneyMode, Quote } from "@openmarketsai/order-sheet";
import {
  buildBookGrid,
  buildTabs,
  consensusLineOf,
  defaultLineKey,
  groupSpreadLines,
  groupTotalLines,
  sideColumnLabel,
  totalLine,
  type MarketLike,
  type MarketShape,
  type PartnerLike,
  type PositionLike,
} from "./books.js";
import { bestQuote, bucketContests, isActionable, isGame, type ContestLike, type Section } from "./marketsBoard.js";
import { hedgeStake, orderFill, positionHashOf, type Ccy, type OrderLike } from "./portfolio.js";

/* ===================================================================== prices */

export const MIN_CENTS = 2;
export const MAX_CENTS = 98;

/** A whole-cent price inside 2–98¢. */
export function clampCents(c: number): number {
  if (!Number.isFinite(c)) return MIN_CENTS;
  return Math.min(MAX_CENTS, Math.max(MIN_CENTS, Math.round(c)));
}

/** Cents → the API's 0–1 price (always inside the band). */
export const centsToPrice = (c: number): number => clampCents(c) / 100;

/**
 * The default bid for a cell: one cent inside the venue's best ask — the highest whole cent
 * strictly below it (49.75¢ → 49¢, 49¢ → 48¢) — clamped to 2–98¢. Null with no usable ask.
 */
export function defaultBidCents(ask: number | null | undefined): number | null {
  if (typeof ask !== "number" || !(ask > 0) || !(ask < 1)) return null;
  return clampCents(Math.ceil(ask * 100 - 1e-9) - 1);
}

/* ==================================================================== markets */

const COMPLEMENT: Record<string, string> = { home: "away", away: "home", over: "under", under: "over", yes: "no", no: "yes" };
const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

/** Exactly two sides, and they're each other's complement. */
function isTwoSided(positions: PositionLike[]): boolean {
  const sides = [...new Set(positions.map((p) => norm(p.side_key)).filter(Boolean))];
  return sides.length === 2 && COMPLEMENT[sides[0]] === sides[1];
}

/** One two-sided book: the two sides of a moneyline, of one line, or of one participant's prop. */
export interface MmBook {
  /** Stable within its market: "main", the line ("-3.5"), or "<participant>|<line>". */
  key: string;
  label: string;
  line: number | null;
  positions: PositionLike[];
}

export interface MmMarket {
  marketKey: string;
  label: string;
  shape: MarketShape;
  books: MmBook[];
  /** The book to open on: the consensus line for spreads/totals, else the first. */
  defaultBook: string | null;
}

const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : "0");

/**
 * The two-sided markets a game offers, in the game page's tab order. Multi-way markets (a
 * 3-way result as one market) don't qualify as a whole; their per-participant yes/no books do.
 */
export function quotableMarkets(positions: PositionLike[], markets: MarketLike[]): MmMarket[] {
  const { tabs } = buildTabs(positions, markets);
  const out: MmMarket[] = [];
  for (const tab of tabs) {
    const sides = new Set(tab.positions.map((p) => norm(p.side_key)));
    let books: MmBook[] = [];
    let defaultBook: string | null = null;
    if (tab.shape === "spread") {
      const lines = groupSpreadLines(tab.positions);
      books = lines
        .filter((l) => isTwoSided(l.positions))
        .map((l) => {
          const home = l.positions.find((p) => norm(p.side_key) === "home");
          return { key: l.key, label: home ? sideColumnLabel(home, "home") : signed(l.line), line: l.line, positions: l.positions };
        });
      defaultBook = defaultLineKey(lines.filter((l) => books.some((b) => b.key === l.key)), consensusLineOf(tab.positions, "spread"));
    } else if (tab.shape === "total") {
      const lines = groupTotalLines(tab.positions);
      books = lines
        .filter((l) => isTwoSided(l.positions))
        .map((l) => ({ key: l.key, label: `O/U ${l.line}`, line: l.line, positions: l.positions }));
      defaultBook = defaultLineKey(lines.filter((l) => books.some((b) => b.key === l.key)), consensusLineOf(tab.positions, "total"));
    } else if (sides.has("home") || sides.has("away")) {
      // A team-vs-team moneyline: one book.
      if (isTwoSided(tab.positions)) books = [{ key: "main", label: tab.label, line: null, positions: tab.positions }];
    } else {
      // Props / 3-way results: one book per participant (+ line), when it has both sides.
      const groups = new Map<string, PositionLike[]>();
      for (const p of tab.positions) {
        const line = totalLine(p.variable);
        const k = `${p.participant_id ?? ""}|${line ?? ""}`;
        let g = groups.get(k);
        if (!g) groups.set(k, (g = []));
        g.push(p);
      }
      books = [...groups.entries()]
        .filter(([, ps]) => isTwoSided(ps))
        .map(([key, ps]) => {
          const name = ps[0].participant_name ?? ps[0].title ?? tab.label;
          const line = totalLine(ps[0].variable);
          return { key, label: line != null && line !== 0 ? `${name} ${line}` : name, line, positions: ps };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    if (books.length === 0) continue;
    out.push({ marketKey: tab.marketKey, label: tab.label, shape: tab.shape, books, defaultBook: defaultBook ?? books[0].key });
  }
  return out;
}

/* ============================================================ quote steps */

/*
 * The New quote flow, one step at a time: game → market → participant (props / per-participant
 * 3-way only) → line → the quote. A step with exactly one choice is skipped (going forward and in
 * the breadcrumb).
 */

/** The participant a book belongs to: the part before "|" in a prop / 3-way book key, else null. */
export function bookParticipantId(bookKey: string | null | undefined): string | null {
  if (!bookKey || !bookKey.includes("|")) return null;
  return bookKey.slice(0, bookKey.indexOf("|"));
}

/** A market whose books are per participant (props, per-participant 3-way). */
export const isPerParticipant = (m: MmMarket): boolean => m.books.some((b) => b.key.includes("|"));

/** Distinct venues with a buyable price on any side of these positions. */
export function venueCount(positions: PositionLike[]): number {
  const s = new Set<string>();
  for (const p of positions) for (const pl of p.partner_liquidities ?? []) if (isActionable(pl)) s.add(pl.partner_id);
  return s.size;
}

export interface MmParticipant {
  id: string;
  name: string;
  books: MmBook[];
  /** Venues quoting any of its books. */
  venues: number;
}

/**
 * A per-participant market's books grouped by participant, most venues quoting first (then by
 * name). Empty for markets that aren't per participant (moneyline, spreads, totals).
 */
export function marketParticipants(m: MmMarket): MmParticipant[] {
  if (!isPerParticipant(m)) return [];
  const g = new Map<string, MmParticipant>();
  for (const b of m.books) {
    const id = bookParticipantId(b.key) ?? "";
    let p = g.get(id);
    if (!p) {
      const first = b.positions[0];
      g.set(id, (p = { id, name: first?.participant_name ?? first?.title ?? b.label, books: [], venues: 0 }));
    }
    p.books.push(b);
  }
  for (const p of g.values()) p.venues = venueCount(p.books.flatMap((b) => b.positions));
  return [...g.values()].sort((a, b) => b.venues - a.venues || a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

/** Both sides' best price on one book (the column order of the ladder). */
export interface LineSide {
  label: string;
  price: number | null;
  american: string | null;
}

export interface MmLineOption {
  book: MmBook;
  /** The default (consensus / most-quoted) line. */
  main: boolean;
  /** What the line reads as after its market (and participant): "Heat −3.5", "O/U 220.5", "25.5". */
  label: string;
  venues: number;
  sides: LineSide[];
}

/** Each side's cheapest buyable price, in column order. */
export function lineSides(book: MmBook): LineSide[] {
  return buildBookGrid(book.positions).columns.map((c) => {
    const best = bestQuote(c.position);
    return { label: c.label, price: typeof best?.price === "number" ? best.price : null, american: best?.american_odds ?? null };
  });
}

/** A line's label once its market (and participant) are already named. */
export function lineLabel(book: MmBook): string {
  if (!book.key.includes("|")) return book.label;
  if (book.line == null || book.line === 0) return book.label;
  const ou = book.positions.some((p) => norm(p.side_key) === "over" || norm(p.side_key) === "under");
  return ou ? `O/U ${book.line}` : String(book.line);
}

/** How balanced a book is (0 = a coin flip): the main line of a prop is the one nearest 50/50. */
function lopsided(sides: LineSide[]): number {
  const ps = sides.map((s) => s.price).filter((p): p is number => p != null);
  return ps.length === 0 ? 1 : Math.max(...ps.map((p) => Math.abs(p - 0.5)));
}

/**
 * The lines left for a market (and, for per-participant markets, one participant), the main
 * line first and marked. The main line is the market's default when it's here (the consensus line
 * on spreads / totals), else the one most venues quote (ties: nearest 50/50). The rest keep the
 * market's order.
 */
export function participantLines(m: MmMarket, participantId: string | null = null): MmLineOption[] {
  const books = isPerParticipant(m) ? m.books.filter((b) => (bookParticipantId(b.key) ?? "") === (participantId ?? "")) : m.books;
  const opts = books.map((book) => ({ book, main: false, label: lineLabel(book), venues: venueCount(book.positions), sides: lineSides(book) }));
  if (opts.length === 0) return [];
  let main = opts.find((o) => o.book.key === m.defaultBook) ?? null;
  if (!main || isPerParticipant(m)) {
    main = [...opts].sort((a, b) => b.venues - a.venues || lopsided(a.sides) - lopsided(b.sides))[0];
  }
  main.main = true;
  return [main, ...opts.filter((o) => o !== main)];
}

/** Markets in the list's order: the moneyline first, then the game page's tab order. */
export function orderMarkets(ms: MmMarket[]): MmMarket[] {
  const first = (m: MmMarket) => (m.shape === "moneyline" && !isPerParticipant(m) ? 0 : 1);
  return ms.map((m, i) => ({ m, i })).sort((a, b) => first(a.m) - first(b.m) || a.i - b.i).map((x) => x.m);
}

/** The count hint on a market row: "2 sides", "9 lines", "14 players", "3 outcomes". */
export function marketHint(m: MmMarket): string {
  const ps = marketParticipants(m);
  if (ps.length > 0) {
    const n = ps.length;
    if (m.books.every((b) => b.line == null || b.line === 0)) return `${n} outcome${n === 1 ? "" : "s"}`;
    return n === 1 ? (ps[0].books.length === 1 ? lineLabel(ps[0].books[0]) : `${ps[0].books.length} lines`) : `${n} players`;
  }
  if (m.books.length > 1) return `${m.books.length} lines`;
  return m.books[0].key === "main" ? "2 sides" : m.books[0].label;
}

export type QuoteStep = "game" | "market" | "participant" | "line" | "quote";

/** What the user has picked so far (null = not yet). */
export interface QuotePick {
  contestId: string | null;
  marketKey: string | null;
  participantId: string | null;
  bookKey: string | null;
}

export interface QuoteFlow {
  /** The step to show. */
  step: QuoteStep;
  market: MmMarket | null;
  participants: MmParticipant[];
  participant: MmParticipant | null;
  lines: MmLineOption[];
  book: MmBook | null;
  /** Steps with more than one choice (shown, and a crumb once chosen). Skipped steps are absent. */
  shown: Exclude<QuoteStep, "game" | "quote">[];
  /** A pick that no longer exists (a re-quote's market / participant / line went away). */
  missing: "market" | "participant" | "line" | null;
}

/**
 * Where the flow stands for a pick. `markets` is null while the game's book loads (the market
 * step shows skeletons). Each step with exactly one choice is taken automatically.
 */
export function resolveQuoteFlow(markets: MmMarket[] | null, pick: QuotePick): QuoteFlow {
  const flow: QuoteFlow = { step: "game", market: null, participants: [], participant: null, lines: [], book: null, shown: [], missing: null };
  if (!pick.contestId) return flow;
  flow.step = "market";
  if (!markets || markets.length === 0) return flow;

  if (markets.length > 1) flow.shown.push("market");
  const market = markets.length === 1 ? markets[0] : (markets.find((m) => m.marketKey === pick.marketKey) ?? null);
  if (!market) {
    if (pick.marketKey) flow.missing = "market";
    return flow;
  }
  flow.market = market;

  flow.participants = marketParticipants(market);
  let pid: string | null = null;
  if (flow.participants.length > 0) {
    if (flow.participants.length > 1) flow.shown.push("participant");
    const p = flow.participants.length === 1 ? flow.participants[0] : (flow.participants.find((x) => x.id === pick.participantId) ?? null);
    if (!p) {
      flow.step = "participant";
      if (pick.participantId != null) flow.missing = "participant";
      return flow;
    }
    flow.participant = p;
    pid = p.id;
  }

  flow.lines = participantLines(market, pid);
  if (flow.lines.length > 1) flow.shown.push("line");
  const line = flow.lines.length === 1 ? flow.lines[0] : (flow.lines.find((l) => l.book.key === pick.bookKey) ?? null);
  if (!line) {
    flow.step = "line";
    if (pick.bookKey) flow.missing = "line";
    return flow;
  }
  flow.book = line.book;
  flow.step = "quote";
  return flow;
}

/** Going back to a step clears it and everything after it. */
export function backTo(pick: QuotePick, step: QuoteStep): QuotePick {
  switch (step) {
    case "game":
      return { contestId: null, marketKey: null, participantId: null, bookKey: null };
    case "market":
      return { ...pick, marketKey: null, participantId: null, bookKey: null };
    case "participant":
      return { ...pick, participantId: null, bookKey: null };
    case "line":
      return { ...pick, bookKey: null };
    default:
      return pick;
  }
}

/** The games the picker lists: head-to-head, with markets. */
export const quotableGames = <C extends ContestLike>(cs: C[]): C[] => cs.filter((c) => isGame(c) && c.has_markets !== false);

/** League pills: each league present with its game count, by name (unnamed ids last). */
export function leagueCounts(games: ContestLike[], nameOf: (id: string) => string | undefined = () => undefined): { id: string; name: string; n: number }[] {
  const n = new Map<string, number>();
  for (const c of games) if (c.league_id) n.set(c.league_id, (n.get(c.league_id) ?? 0) + 1);
  return [...n.entries()]
    .map(([id, count]) => ({ id, name: nameOf(id) ?? "", n: count }))
    .sort((a, b) => (a.name ? 0 : 1) - (b.name ? 0 : 1) || a.name.localeCompare(b.name) || b.n - a.n || a.id.localeCompare(b.id));
}

/**
 * The picker's sections from start times alone (no live state here): In progress (started up to
 * 4 h ago) · Starting soon · Today · Tomorrow · Later · Started earlier. `leagueId` filters first.
 */
export function gameSections<C extends ContestLike>(games: C[], leagueId: string | null, now: number): Section<C>[] {
  const shown = leagueId ? games.filter((c) => c.league_id === leagueId) : games;
  return bucketContests(shown, () => null, now).map((s) => (s.key === "live" ? { ...s, label: "In progress" } : s));
}

/** How a side reads as an outcome:"Hawks win", "Over 220.5", "Hawks −3.5 covers", "LeBron Yes". */
export function outcomeLabel(shape: MarketShape, column: string, side: string, participant?: string | null): string {
  const s = norm(side);
  if (shape === "moneyline" && (s === "home" || s === "away")) return `${column} win`;
  if (shape === "spread") return `${column} covers`;
  if ((s === "yes" || s === "no") && participant) return `${participant}: ${column}`;
  return column;
}

/* ===================================================================== ladder */

export interface LadderColumn {
  side: string;
  label: string;
  outcome: string;
  positionHash: string;
  consensus: number | null;
}

export interface LadderCell {
  /** `${partnerId}|${side}` */
  key: string;
  partnerId: string;
  partnerName: string;
  col: 0 | 1;
  side: string;
  positionHash: string;
  liquidityHash: string | null;
  /** The venue's current best (ask) for this side, 0–1. */
  ask: number | null;
  american: string | null;
  /** Maker fee as a fraction of the stake, from the venue's fee_packet (0 when absent). */
  feeRate: number;
  /** The highest bid that rests here (best ask − 1¢, 2–98¢); null when there's nothing to bid against. */
  bid: number | null;
  enabled: boolean;
  /** Why the cell is disabled. */
  why: string | null;
}

export interface Ladder {
  columns: [LadderColumn, LadderColumn];
  rows: { partnerId: string; partnerName: string; cells: [LadderCell, LadderCell] }[];
}

export interface LadderScope {
  mode: MoneyMode;
  /** Real: the connected venues (an empty set = none). Paper: null (every venue). */
  allowed: ReadonlySet<string> | null;
  /** Venues that take limit orders (Real only); null = unknown, don't filter. */
  limitVenues: ReadonlySet<string> | null;
}

export const cellKey = (partnerId: string, side: string): string => `${partnerId}|${side}`;

/**
 * One book as a venue × side ladder. Real mode keeps only the connected venues (the same
 * scoping as Markets) and disables venues that don't take limit orders; Paper allows every
 * venue (the orders rest on the practice book). A cell with no actionable price is disabled.
 */
export function buildLadder(book: MmBook, shape: MarketShape, scope: LadderScope): Ladder | null {
  const grid = buildBookGrid(book.positions);
  if (grid.columns.length !== 2) return null;
  const columns = grid.columns.map((c) => ({
    side: c.side,
    label: c.label,
    outcome: outcomeLabel(shape, c.label, c.side, c.position.participant_name),
    positionHash: c.position.position_hash,
    consensus: c.consensusPrice,
  })) as [LadderColumn, LadderColumn];
  const real = scope.mode === "real";
  const rows = grid.rows
    .filter((r) => !real || !scope.allowed || scope.allowed.has(r.partnerId))
    .map((r) => {
      const cells = r.cells.map((pl, i): LadderCell => {
        const col = grid.columns[i];
        const actionable = isActionable(pl);
        const ask = actionable && typeof pl?.price === "number" ? pl.price : null;
        const bid = defaultBidCents(ask);
        const noLimit = real && scope.limitVenues != null && !scope.limitVenues.has(r.partnerId);
        const why = !pl ? "Not quoted here" : !actionable || bid == null ? "No price right now" : noLimit ? `${r.partnerName} doesn't take limit orders` : null;
        return {
          key: cellKey(r.partnerId, col.side),
          partnerId: r.partnerId,
          partnerName: r.partnerName,
          col: i as 0 | 1,
          side: col.side,
          positionHash: col.position.position_hash,
          liquidityHash: (pl as PartnerLike | undefined)?.liquidity_hash ?? null,
          ask,
          american: pl?.american_odds ?? null,
          feeRate: makerFeeRate((pl as { fee_packet?: FeePacketLike | null } | undefined)?.fee_packet),
          bid,
          enabled: why == null,
          why,
        };
      }) as [LadderCell, LadderCell];
      return { partnerId: r.partnerId, partnerName: r.partnerName, cells };
    });
  return { columns, rows };
}

/* ======================================================================= plan */

/*
 * Revision 1 (docs/market-making-ux.md): the user sets a MARGIN (after fees) and CONTRACTS per
 * side; greenbook computes the two bids. Everything below is in whole cents and fractions of the
 * $1 a contract pays.
 *
 *   margin    per pair of contracts (one on each side, exactly one of which pays $1):
 *             margin = 100¢ − (bid A × (1 + fee A) + bid B × (1 + fee B))
 *             so "2%" means 2¢ kept on every $1 paid out, whoever wins.
 *   fair      the consensus prices normalised so the two sides sum to 1.
 *   centring  each side's all-in cost is its fair price minus half the margin.
 *   clamps    a bid may not reach the venue's ask (it would take instead of resting): it stops at
 *             1¢ under it and the leftover moves to the other side. Every bid stays inside 2–98¢.
 */

/** Margin pills (percent of the $1 payout). */
export const MARGIN_PRESETS = [1, 2, 3, 5] as const;
export const DEFAULT_MARGIN_PCT = 2;
export const DEFAULT_CONTRACTS = 10;
/** Custom margins outside this are refused (a 0% quote earns nothing; 99% can't be bid). */
export const MAX_MARGIN_PCT = 99;

/** The fee shape the liquidity feed carries per venue (`fee_packet`). */
export interface FeePacketLike {
  taker_price?: number | null;
  maker_price?: number | null;
  /** Absolute fee on a canonical $100 stake (order-sheet `FeePacket`). */
  taker_fee?: number | null;
  maker_fee?: number | null;
}

/**
 * A venue's maker fee as a fraction of the stake: `maker_fee` is the fee on a $100 stake, so
 * 1.11 → 0.0111. It's quoted at the venue's CURRENT price; greenbook scales it linearly to the
 * bid (the same estimate the order-sheet SDK makes). 0 when the packet is missing or nonsense.
 */
export function makerFeeRate(packet: FeePacketLike | null | undefined): number {
  const f = packet?.maker_fee;
  return typeof f === "number" && Number.isFinite(f) && f > 0 && f < 100 ? f / 100 : 0;
}

/** Consensus prices normalised so the two sides sum to 1. Null without two positive prices. */
export function fairPrices(a: number | null | undefined, b: number | null | undefined): [number, number] | null {
  if (typeof a !== "number" || typeof b !== "number" || !(a > 0) || !(b > 0) || !Number.isFinite(a + b)) return null;
  const f = a / (a + b);
  return [f, 1 - f];
}

/** Margin per pair after fees, as a fraction of the $1 payout (0.02 = 2%). Negative = a loss if both fill. */
export function pairMargin(cents: readonly [number, number], feeRates: readonly [number, number] = [0, 0]): number {
  return (100 - cents[0] * (1 + feeRates[0]) - cents[1] * (1 + feeRates[1])) / 100;
}

export interface PlanSide {
  /** The highest bid that still rests: 1¢ under the picked venue's ask (see `defaultBidCents`). */
  capCents: number;
  /** Maker fee as a fraction of the stake (0 in Paper: practice orders pay none). */
  feeRate: number;
}

/** Why a bid isn't where the fair price put it. */
export type BidClamp = "ask" | "floor" | "ceiling";

export interface BidPlan {
  cents: [number, number];
  /** The margin the bids actually give (fraction of $1). */
  margin: number;
  /** Per side: why the bid was moved off its centred spot, if it was. */
  clamped: [BidClamp | null, BidClamp | null];
  /** The target can't be reached (the bids hit 2¢): `margin` is the most there is. */
  short: boolean;
  /** Clamps left more margin than asked for (the market is wide or crossed). */
  over: boolean;
}

const EPS = 1e-9;

/**
 * The two bids for a target margin (fraction of $1), centred on the fair price:
 *   1. all-in cost per side = fair × 100 − margin ÷ 2 (cents);
 *   2. clamp each side's cost to what its bid allows (2¢ … min(98¢, ask − 1¢), plus its fee); what
 *      a clamp removes from one side goes onto the other (then that side is clamped too);
 *   3. bids = cost ÷ (1 + fee), floored to whole cents, then raised 1¢ at a time (largest
 *      remainder first) while the margin stays at or above the target.
 */
export function planBids(fair: readonly [number, number], targetMargin: number, sides: readonly [PlanSide, PlanSide]): BidPlan {
  const M = Math.max(0, targetMargin) * 100;
  const k = [1 + sides[0].feeRate, 1 + sides[1].feeRate];
  const hiBid = [Math.max(MIN_CENTS, Math.min(MAX_CENTS, sides[0].capCents)), Math.max(MIN_CENTS, Math.min(MAX_CENTS, sides[1].capCents))];
  const lo = [MIN_CENTS * k[0], MIN_CENTS * k[1]];
  const hi = [hiBid[0] * k[0], hiBid[1] * k[1]];
  const want = [fair[0] * 100 - M / 2, fair[1] * 100 - M / 2];
  const cost = [...want];
  const clamped: [BidClamp | null, BidClamp | null] = [null, null];
  const clampSide = (i: 0 | 1): number => {
    const c = cost[i];
    if (c > hi[i] + EPS) {
      cost[i] = hi[i];
      clamped[i] = sides[i].capCents < MAX_CENTS ? "ask" : "ceiling";
    } else if (c < lo[i] - EPS) {
      cost[i] = lo[i];
      clamped[i] = "floor";
    }
    return c - cost[i];
  };
  // Clamp side 0, hand its leftover to side 1, clamp side 1, hand that back once.
  cost[1] += clampSide(0);
  cost[0] += clampSide(1);
  clampSide(0);

  const raw = [cost[0] / k[0], cost[1] / k[1]];
  const cents: [number, number] = [0, 0];
  for (const i of [0, 1] as const) cents[i] = Math.min(hiBid[i], Math.max(MIN_CENTS, Math.floor(raw[i] + EPS)));
  const fees: [number, number] = [sides[0].feeRate, sides[1].feeRate];
  const target = M / 100;
  // Give back the rounding: largest remainder first, never below the target margin.
  const order = ([0, 1] as const).slice().sort((a, b) => raw[b] - Math.floor(raw[b] + EPS) - (raw[a] - Math.floor(raw[a] + EPS)));
  for (const i of order) {
    const next: [number, number] = [...cents];
    next[i] += 1;
    if (next[i] <= hiBid[i] && pairMargin(next, fees) >= target - EPS) cents[i] = next[i];
  }
  const margin = pairMargin(cents, fees);
  return { cents, margin, clamped, short: margin < target - 0.005, over: margin > target + 0.015 };
}

/** The stake a limit for `contracts` at `cents` needs, rounded to cents (10 × 37¢ → 3.70). */
export function contractsToAmount(contracts: number, cents: number): number {
  if (!(contracts > 0) || !(cents > 0)) return 0;
  return Math.round(contracts * clampCents(cents)) / 100;
}

/** `total` whole contracts over `ways` venues: as even as possible, the remainder to the first. */
export function splitContracts(total: number, ways: number): number[] {
  const n = Math.max(0, Math.floor(total));
  const w = Math.max(1, Math.floor(ways));
  const base = Math.floor(n / w);
  return Array.from({ length: w }, (_, i) => base + (i < n % w ? 1 : 0));
}

/** One bid the quote will place. */
export interface QuoteLeg {
  key: string;
  col: 0 | 1;
  sideLabel: string;
  partnerId: string;
  partnerName: string;
  positionHash: string;
  liquidityHash: string | null;
  cents: number;
  /** Whole contracts. */
  contracts: number;
  /** Maker fee as a fraction of the stake (0 in Paper). */
  feeRate: number;
}

/** What the opportunity already holds (a re-quote): filled contracts per side and their cost, fees in. */
export interface Existing {
  filled: [number, number];
  cost: number;
}

export interface QuoteResult {
  /** New contracts per side if every bid fills. */
  contracts: [number, number];
  /** New money if every bid fills, fees included. */
  cost: number;
  fees: number;
  /** Payout per outcome if every bid fills (existing fills included). */
  bothFill: [number, number];
  /**
   * If only one side's bids fill: `onlyFill[s][w]` is the payout when side `s` filled and side `w`
   * wins (existing fills included).
   */
  onlyFill: [[number, number], [number, number]];
}

/** A leg's stake and estimated maker fee. */
export function quoteLegCost(l: Pick<QuoteLeg, "contracts" | "cents" | "feeRate">): { stake: number; fee: number } {
  const stake = contractsToAmount(l.contracts, l.cents);
  return { stake, fee: Math.round(stake * l.feeRate * 100) / 100 };
}

/** The result line: payout per outcome if both sides fill, and if only one does. */
export function quoteResult(legs: QuoteLeg[], existing: Existing = { filled: [0, 0], cost: 0 }): QuoteResult {
  const n: [number, number] = [0, 0];
  const c: [number, number] = [0, 0];
  let fees = 0;
  for (const l of legs) {
    if (!(l.contracts > 0)) continue;
    const { stake, fee } = quoteLegCost(l);
    n[l.col] += l.contracts;
    c[l.col] += stake + fee;
    fees += fee;
  }
  const e = existing;
  const held = (extra: [number, number]): [number, number] => [e.filled[0] + extra[0], e.filled[1] + extra[1]];
  const pay = (h: [number, number], spent: number): [number, number] => [h[0] - spent, h[1] - spent];
  return {
    contracts: n,
    cost: c[0] + c[1],
    fees,
    bothFill: pay(held(n), e.cost + c[0] + c[1]),
    onlyFill: [pay(held([n[0], 0]), e.cost + c[0]), pay(held([0, n[1]]), e.cost + c[1])],
  };
}

/**
 * The quote as ONE multi-leg buy: each leg venue-directed by its `liquidity_hash` (or position +
 * partner when the feed gave no hash), a limit at the bid for `contracts × bid` dollars, and an
 * EXPLICIT mode (the API treats an omitted mode as real money). Legs with no contracts are dropped.
 *
 * `amount`, not `shares`: `amount` limits are what we probed live (paper + real), and with a whole-
 * cent bid `contracts × bid` is exact, so the order is still exactly `contracts` contracts.
 */
export function buildQuoteLegs(legs: QuoteLeg[], mode: MoneyMode): BuyLeg[] {
  return legs
    .filter((l) => l.contracts > 0)
    .map((l) => ({
      ...(l.liquidityHash ? { liquidity_hash: l.liquidityHash } : { position_hash: l.positionHash, partner_id: l.partnerId }),
      amount: contractsToAmount(l.contracts, l.cents),
      max_price: centsToPrice(l.cents),
      mode,
      order_type: "limit" as const,
    }));
}

/* ------------------------------------------------------------- side venues */

/** One venue a side can bid at. */
export interface SideVenue {
  partnerId: string;
  partnerName: string;
  liquidityHash: string | null;
  /** The venue's current price for this side (0–1). */
  ask: number;
  american: string | null;
  /** 1¢ under the ask: the highest bid that rests. */
  capCents: number;
  /** Maker fee fraction from the venue's fee_packet (applied in Real only). */
  feeRate: number;
}

/** The venues a side can bid at, highest price first (the pick that sits closest to the trading). */
export function sideVenues(ladder: Ladder, col: 0 | 1): SideVenue[] {
  const out: SideVenue[] = [];
  for (const r of ladder.rows) {
    const c = r.cells[col];
    if (!c.enabled || c.ask == null || c.bid == null) continue;
    out.push({ partnerId: c.partnerId, partnerName: c.partnerName, liquidityHash: c.liquidityHash, ask: c.ask, american: c.american, capCents: c.bid, feeRate: c.feeRate });
  }
  return out.sort((a, b) => b.ask - a.ask || a.partnerName.localeCompare(b.partnerName));
}

/** The fair price for a ladder: the consensus, normalised; else the two picked venues' prices, normalised. */
export function fairFor(ladder: Ladder, picked: readonly [SideVenue | undefined, SideVenue | undefined]): [number, number] | null {
  return fairPrices(ladder.columns[0].consensus, ladder.columns[1].consensus) ?? fairPrices(picked[0]?.ask, picked[1]?.ask);
}

/** The default quote for a ladder (the explainer's worked example, and the builder's starting point). */
export function defaultPlan(
  ladder: Ladder,
  mode: MoneyMode,
  marginPct = DEFAULT_MARGIN_PCT,
): { venues: [SideVenue, SideVenue]; fair: [number, number]; plan: BidPlan } | null {
  const v0 = sideVenues(ladder, 0)[0];
  const v1 = sideVenues(ladder, 1)[0];
  if (!v0 || !v1) return null;
  const fair = fairFor(ladder, [v0, v1]);
  if (!fair) return null;
  const fee = (v: SideVenue) => (mode === "real" ? v.feeRate : 0);
  const plan = planBids(fair, marginPct / 100, [
    { capCents: v0.capCents, feeRate: fee(v0) },
    { capCents: v1.capCents, feeRate: fee(v1) },
  ]);
  return { venues: [v0, v1], fair, plan };
}

/* ==================================================================== results */

/** What greenbook remembers about one leg it placed: the side and the venue it targeted. */
export interface LegRef {
  positionHash: string;
  partnerId: string;
  partnerName: string;
}

export type LegOutcome = "resting" | "filled" | "partial" | "pending" | "failed";

export interface PlacedLeg {
  ref: LegRef;
  label: string;
  cents: number | null;
  amount: number;
  outcome: LegOutcome;
  reason: string | null;
  routerOrderIds: string[];
}

/** Each sent leg's result (resting / filled / failed with the reason), aligned with what was sent. */
export function placedLegs(
  res: BuyResponse,
  sent: { ref: LegRef; label: string; cents: number | null; amount: number }[],
): PlacedLeg[] {
  return sent.map((s, i) => {
    const r = res.results.find((x) => x.index === i) ?? res.results[i];
    if (!r) return { ...s, outcome: "failed", reason: res.halted_at_index != null && i > res.halted_at_index ? "Not sent (halted)" : "No result returned", routerOrderIds: [] };
    const ids = (r.venues ?? []).map((v) => v.router_order_id).filter((x): x is string => !!x);
    const outcome: LegOutcome =
      r.status === "failed" || r.status === "rejected"
        ? "failed"
        : r.status === "resting"
          ? r.filled_shares > 0 || r.filled_notional > 0 ? "partial" : "resting"
          : r.status === "pending"
            ? "pending"
            : r.unfilled_notional > 0 && r.filled_notional > 0 ? "partial" : "filled";
    const venueErr = (r.venues ?? []).map((v) => v.error).find((e) => !!e) ?? null;
    const reason = outcome === "failed" ? (r.message_user ?? r.rejected_reason ?? venueErr ?? "Refused") : null;
    return { ...s, outcome, reason, routerOrderIds: ids };
  });
}

/** router_order_id → the leg it was, for the registry. */
export function legRefsFrom(placed: PlacedLeg[]): Record<string, LegRef> {
  const out: Record<string, LegRef> = {};
  for (const p of placed) for (const id of p.routerOrderIds) out[id] = p.ref;
  return out;
}

/* ======================================================================= book */

/** An opportunity leg (an order row inside `/auth/opportunities`). */
export interface MmLeg extends OrderLike {
  router_order_id: string;
  fee?: number | null;
  failure_reason?: string | null;
}

export interface MmSide {
  positionHash: string;
  /** Column label: "Hawks", "Over 220.5", "Yes". */
  label: string;
  /** "Hawks win", "Over 220.5", "Hawks −3.5 covers". */
  outcome: string;
}

const tsOf = (iso: string | null | undefined): number => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
};

/** Legs oldest first. `leg_number` restarts on every request, so order by `created_at`. */
export function sortLegs<L extends MmLeg>(legs: L[]): L[] {
  return [...legs].sort(
    (a, b) => tsOf(a.created_at ?? a.placed_datetime) - tsOf(b.created_at ?? b.placed_datetime) || a.router_order_id.localeCompare(b.router_order_id),
  );
}

/** Still resting: an open limit with an unfilled remainder and no result. */
export function isResting(l: MmLeg): boolean {
  const st = norm(l.status);
  if (st !== "open" && st !== "pending" && st !== "resting") return false;
  if (l.result) return false;
  if (norm(l.fill_status) === "filled") return false;
  const unfilled = l.unfilled_amt ?? Math.max(0, (l.amount ?? 0) - (l.filled_amt ?? 0));
  return unfilled > 0;
}

/** Which side a leg is on: the registry's record, else its position, else its title. */
export function legSide(l: MmLeg, sides: readonly [MmSide, MmSide], refs: Record<string, LegRef>, titles?: readonly [string[], string[]]): 0 | 1 | null {
  const ref = refs[l.router_order_id];
  const hash = ref?.positionHash ?? positionHashOf(l);
  if (hash === sides[0].positionHash) return 0;
  if (hash === sides[1].positionHash) return 1;
  const t = norm(l.title);
  if (t && titles) {
    if (titles[0].some((x) => norm(x) === t)) return 0;
    if (titles[1].some((x) => norm(x) === t)) return 1;
  }
  return null;
}

/** A leg's filled cost: the filled stake plus its fee (pro rata to the filled part) when the row carries one. */
export function legCost(l: MmLeg): { stake: number; fee: number; contracts: number } {
  const f = orderFill(l);
  if (!(f.contracts > 0)) return { stake: 0, fee: 0, contracts: 0 };
  const amount = l.amount ?? 0;
  const share = amount > 0 ? Math.min(1, f.stake / amount) : 1;
  const fee = typeof l.fee === "number" && l.fee > 0 ? l.fee * share : 0;
  return { stake: f.stake, fee, contracts: f.contracts };
}

export interface SideBook {
  side: MmSide;
  legs: MmLeg[];
  /** Contracts filled (each pays 1 if this side wins). */
  filled: number;
  /** Contracts if every leg on this side filled. */
  quoted: number;
  /** Filled cost including fees. */
  cost: number;
  /** Average fill price (stake ÷ contracts), null with nothing filled. */
  avgPrice: number | null;
}

export interface OppBook {
  sides: [SideBook, SideBook];
  /** Legs greenbook can't place on a side (still counted in the cost). */
  unmatched: MmLeg[];
  /** Every filled leg's cost, fees included. */
  cost: number;
  fees: number;
  /** Payout if side 0 / side 1 wins: its contracts × 1 − the total cost. */
  payout: [number, number];
  resting: MmLeg[];
  /** Filled contracts on side 0 minus side 1. */
  diff: number;
  /** Every filled leg has settled. */
  settled: boolean;
}

export function buildOppBook(
  sides: readonly [MmSide, MmSide],
  legs: MmLeg[],
  refs: Record<string, LegRef> = {},
  titles?: readonly [string[], string[]],
): OppBook {
  const mk = (side: MmSide): SideBook => ({ side, legs: [], filled: 0, quoted: 0, cost: 0, avgPrice: null });
  const book: [SideBook, SideBook] = [mk(sides[0]), mk(sides[1])];
  const unmatched: MmLeg[] = [];
  let cost = 0;
  let fees = 0;
  let filledLegs = 0;
  let settledLegs = 0;
  const stakes = [0, 0];
  for (const l of sortLegs(legs)) {
    const c = legCost(l);
    cost += c.stake + c.fee;
    fees += c.fee;
    if (c.contracts > 0) {
      filledLegs++;
      if (l.result) settledLegs++;
    }
    const i = legSide(l, sides, refs, titles);
    if (i == null) {
      unmatched.push(l);
      continue;
    }
    const s = book[i];
    s.legs.push(l);
    s.filled += c.contracts;
    s.cost += c.stake + c.fee;
    stakes[i] += c.stake;
    const price = l.price ?? 0;
    const failed = norm(l.status) === "failed" || norm(l.result) === "cancelled" || norm(l.result) === "void";
    // Quoted = what this side holds if every leg fills: the full order, or what filled before a cancel.
    s.quoted += failed ? c.contracts : price > 0 ? Math.max(c.contracts, (l.amount ?? 0) / price) : c.contracts;
  }
  for (const i of [0, 1] as const) book[i].avgPrice = book[i].filled > 0 ? stakes[i] / book[i].filled : null;
  return {
    sides: book,
    unmatched,
    cost,
    fees,
    payout: [book[0].filled - cost, book[1].filled - cost],
    resting: sortLegs(legs).filter(isResting),
    diff: book[0].filled - book[1].filled,
    settled: filledLegs > 0 && settledLegs === filledLegs,
  };
}

/** A resting bid carried into a re-quote: its side, the venue it targeted, its price and what's left of it. */
export interface RequoteBid {
  col: 0 | 1;
  partnerId: string;
  cents: number;
  /** Whole contracts still resting (unfilled stake ÷ price). */
  contracts: number;
}

/**
 * What a re-quote opens with: the opportunity's resting bids (side, venue, price, contracts left)
 * and what it already holds (filled contracts per side + their cost, fees in).
 */
export function requoteFrom(entry: { legs: Record<string, LegRef> }, book: OppBook): { bids: RequoteBid[]; restingIds: string[]; existing: Existing } {
  const bids: RequoteBid[] = [];
  for (const l of book.resting) {
    const ref = entry.legs[l.router_order_id];
    const col = book.sides[0].legs.includes(l) ? 0 : book.sides[1].legs.includes(l) ? 1 : null;
    const price = l.price ?? 0;
    if (!ref || col == null || !(price > 0)) continue;
    const left = l.unfilled_amt ?? Math.max(0, (l.amount ?? 0) - (l.filled_amt ?? 0));
    const contracts = Math.round(left / price);
    if (contracts > 0) bids.push({ col, partnerId: ref.partnerId, cents: clampCents(price * 100), contracts });
  }
  return {
    bids,
    restingIds: book.resting.map((l) => l.router_order_id),
    existing: { filled: [book.sides[0].filled, book.sides[1].filled], cost: book.cost },
  };
}

/* ===================================================================== status */

export type MmStatus = "working" | "exposed" | "balanced" | "closed";

/** Contracts within this of each other count as balanced (venues fill in fractions). */
export const BALANCE_TOL = 0.5;

/**
 * Working: nothing filled yet. Exposed: fills are uneven. Balanced: equal contracts on both
 * sides. Closed: nothing resting and nothing open (all cancelled, or settled).
 */
export function classifyStatus(book: OppBook): MmStatus {
  const held = book.sides[0].filled + book.sides[1].filled;
  if (book.resting.length === 0 && (held <= 0 || book.settled)) return "closed";
  if (held <= 0) return "working";
  return Math.abs(book.diff) < BALANCE_TOL ? "balanced" : "exposed";
}

export const STATUS_LABEL: Record<MmStatus, string> = { working: "Working", exposed: "Exposed", balanced: "Balanced", closed: "Closed" };

/**
 * The status in plain words, for the line under the chip: "You're mostly betting on Dallas" when
 * exposed (the side holding more contracts), and what Working / Balanced / Closed mean.
 */
export function statusLine(status: MmStatus, book: OppBook): string {
  switch (status) {
    case "working":
      return "Your bids are waiting. Nothing has filled yet.";
    case "exposed": {
      const long = book.diff > 0 ? book.sides[0] : book.sides[1];
      return `You're mostly betting on ${long.side.label}`;
    }
    case "balanced":
      return "Both sides filled evenly, so the result is locked in.";
    case "closed":
      return "Nothing is resting or open.";
  }
}

/** List order: exposed first, then newest first. */
export function sortOpportunities<T extends { status: MmStatus; createdAt: number; id: string }>(items: T[]): T[] {
  return [...items].sort(
    (a, b) => Number(b.status === "exposed") - Number(a.status === "exposed") || b.createdAt - a.createdAt || a.id.localeCompare(b.id),
  );
}

/**
 * The payout per outcome weighted by the current consensus prices (normalised so the two sum to
 * 1). Null without both prices. An estimate, labelled "est." on screen.
 */
export function expectedPayout(payout: readonly [number, number], probs: readonly [number | null | undefined, number | null | undefined]): number | null {
  const [p0, p1] = probs;
  if (typeof p0 !== "number" || typeof p1 !== "number" || !(p0 > 0) || !(p1 > 0)) return null;
  const w = p0 / (p0 + p1);
  return payout[0] * w + payout[1] * (1 - w);
}

/* ====================================================================== hedge */

export interface HedgeNeed {
  /** The side with more contracts. */
  long: 0 | 1;
  short: 0 | 1;
  /** Contracts to buy on the short side to flatten. */
  contracts: number;
}

/** The flattening trade: `d` = long contracts − short contracts, bought on the short side. Null when balanced. */
export function hedgeNeed(book: OppBook): HedgeNeed | null {
  const d = book.diff;
  if (!(Math.abs(d) >= BALANCE_TOL)) return null;
  return d > 0 ? { long: 0, short: 1, contracts: d } : { long: 1, short: 0, contracts: -d };
}

export interface HedgeOrder {
  /** Stake sent. */
  amount: number;
  /** The leg's price ceiling (0–1). */
  maxPrice: number;
  /** The price the stake was sized at (0–1). */
  price: number;
  /** Contracts bought if it fills at `price`. */
  contracts: number;
}

/**
 * Size and price the card's get-out estimate: a market order sized at the venue's best ask, with
 * a ceiling 1¢ above it. The stake is `hedgeStake` (contracts × price, cents; whole coins for
 * ATLAS). Null when there's no usable price. (The order itself is sized and priced by the order
 * sheet: see `sheetFigures`.)
 */
export function hedgeOrder(contracts: number, bestAsk: number | null | undefined, ccy: Ccy): HedgeOrder | null {
  if (!(contracts > 0)) return null;
  if (typeof bestAsk !== "number" || !(bestAsk > 0) || !(bestAsk < 1)) return null;
  const price = bestAsk;
  const maxPrice = centsToPrice(Math.ceil(bestAsk * 100 - 1e-9) + 1);
  const amount = hedgeStake(contracts, price, ccy);
  if (!(amount > 0)) return null;
  return { amount, maxPrice, price, contracts: amount / price };
}

export interface HedgePreview {
  /** Payout per outcome after the hedge fills. */
  payout: [number, number];
  min: number;
  max: number;
  /** Both outcomes ≥ 0: the hedge locks in at least `min`. */
  breaksEven: boolean;
}

/** The payout per outcome if the hedge fills: `added` contracts on the short side for `amount`. */
export function hedgePreview(book: OppBook, short: 0 | 1, amount: number, added: number): HedgePreview {
  const cost = book.cost + amount;
  const payout: [number, number] = [book.sides[0].filled - cost, book.sides[1].filled - cost];
  payout[short] += added;
  const min = Math.min(payout[0], payout[1]);
  return { payout, min, max: Math.max(payout[0], payout[1]), breaksEven: min >= -0.005 };
}

/* ============================================================ single-order sheets */

/*
 * Get out, Move bid and the custom hedge are single orders placed through order-sheet sheets
 * (0.1.2: `opportunityId` joins the opportunity). The confirm shows the sheet's own numbers
 * (exact once its preflight answers) and re-checks them just before sending.
 */

/** What a single-order confirm shows, read off the sheet's quote. */
export interface SheetFigures {
  partnerId: string;
  /** Market: the expected fill (preflight) or the venue quote; limit: the limit. */
  price: number;
  /** The ceiling that will be sent. */
  maxPrice: number;
  contracts: number;
  /** What books, before fees. */
  cost: number;
  /** The fee this order pays: maker when it would rest, else taker. 0 when none / unknown. */
  fee: number;
  /** The server's numbers (preflight), not the sheet's local estimate. */
  exact: boolean;
  wouldRest: boolean;
}

/** The sheet quote → the confirm's numbers. Null without a quote that buys anything. */
export function sheetFigures(q: Pick<Quote, "venuePartnerId" | "price" | "maxPrice" | "contracts" | "cost" | "fee" | "freshness" | "wouldRest"> | null | undefined): SheetFigures | null {
  if (!q || !(q.contracts > 0)) return null;
  const f = q.fee ? (q.wouldRest ? q.fee.maker : q.fee.taker) : 0;
  return {
    partnerId: q.venuePartnerId,
    price: q.price,
    maxPrice: q.maxPrice,
    contracts: q.contracts,
    cost: q.cost,
    fee: Number.isFinite(f) && f > 0 ? f : 0,
    exact: q.freshness === "preflight",
    wouldRest: q.wouldRest,
  };
}

/** Did the order change between what the user saw and what would be sent (venue, price, size, all-in cost)? */
export function figuresMoved(a: SheetFigures | null, b: SheetFigures | null): boolean {
  if (!a || !b) return a !== b;
  return (
    a.partnerId !== b.partnerId ||
    Math.abs(a.price - b.price) >= 0.00005 ||
    Math.abs(a.maxPrice - b.maxPrice) > EPS ||
    Math.abs(a.contracts - b.contracts) > 1e-6 ||
    Math.abs(a.cost + a.fee - (b.cost + b.fee)) >= 0.005
  );
}

/** A contracts size as the sheet's `{ shares }` stake: two decimals (the sheet floors whole-contract venues itself). */
export function sheetShares(contracts: number): number | null {
  const n = Math.round(contracts * 100) / 100;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** The payout per outcome once the sheet's order fills: its contracts on `short`, for its cost + fee. */
export function sheetPreview(book: OppBook, short: 0 | 1, f: Pick<SheetFigures, "cost" | "fee" | "contracts">): HedgePreview {
  return hedgePreview(book, short, f.cost + f.fee, f.contracts);
}

/* ======================================================================== KPI */

/*
 * Revision 2 (docs/market-making-ux.md): ONE big live number per opportunity, whose colour says
 * how settled it is.
 *
 *   draft     builder, nothing placed: the result if both sides fill (`draftKpi`)
 *   working   bids resting, nothing filled: the same "if every bid fills" result (`ifAllFill`)
 *   exposed   fills are uneven: "Get out now" = the locked result if you buy the short side's
 *             missing contracts right now at the best allowed LIVE venue price, taker fee ≈ in
 *             (`getOut`). Venue prices, never consensus: the stream patches venue prices only.
 *   locked    equal contracts on both sides: the result whoever wins
 *   settled   every filled leg settled (the final P&L), or closed with nothing filled ($0)
 */

export type KpiState = "draft" | "working" | "exposed" | "locked" | "settled";

export const KPI_LABEL: Record<KpiState, string> = { draft: "Draft", working: "Working", exposed: "Exposed", locked: "Locked", settled: "Settled" };

/** A card's state (never "draft": that's the builder's). */
export function kpiState(book: OppBook): Exclude<KpiState, "draft"> {
  const held = book.sides[0].filled + book.sides[1].filled;
  if (book.settled) return "settled";
  if (held <= 0) return book.resting.length > 0 ? "working" : "settled";
  return Math.abs(book.diff) < BALANCE_TOL ? "locked" : "exposed";
}

/** The builder's Draft number: the worse of the two outcomes if every bid fills. */
export function draftKpi(result: Pick<QuoteResult, "bothFill">): number {
  return Math.min(result.bothFill[0], result.bothFill[1]);
}

const unfilledOf = (l: MmLeg): number => l.unfilled_amt ?? Math.max(0, (l.amount ?? 0) - (l.filled_amt ?? 0));

/**
 * The payout per outcome if the given resting legs fill (each adds its unfilled stake ÷ price in
 * contracts to its side, and the stake to the cost). Maker fees aren't on a row until it fills,
 * so they're left out (≈).
 */
function payoutIfFill(book: OppBook, legs: (l: MmLeg, side: 0 | 1) => boolean): { payout: [number, number]; contracts: [number, number]; amount: number } {
  const add: [number, number] = [0, 0];
  let amount = 0;
  for (const i of [0, 1] as const) {
    for (const l of book.sides[i].legs) {
      const price = l.price ?? 0;
      if (!book.resting.includes(l) || !(price > 0) || !legs(l, i)) continue;
      const left = unfilledOf(l);
      add[i] += left / price;
      amount += left;
    }
  }
  const cost = book.cost + amount;
  return { payout: [book.sides[0].filled + add[0] - cost, book.sides[1].filled + add[1] - cost], contracts: add, amount };
}

/** Working: the payout per outcome if every resting bid fills. */
export function ifAllFill(book: OppBook): [number, number] {
  return payoutIfFill(book, () => true).payout;
}

/**
 * Settled: the final P&L. The winner is the side holding a leg that won (or the other side of
 * one that lost); null when the rows don't say. Closed with nothing filled is $0.
 */
export function settledResult(book: OppBook): number | null {
  if (book.sides[0].filled + book.sides[1].filled <= 0) return 0;
  for (const i of [0, 1] as const) {
    const filled = book.sides[i].legs.filter((l) => legCost(l).contracts > 0);
    if (filled.some((l) => norm(l.result) === "win")) return book.payout[i];
    if (filled.some((l) => norm(l.result) === "lose")) return book.payout[i === 0 ? 1 : 0];
  }
  return null;
}

/** One venue's live price for a side. */
export interface LiveQuote {
  partnerId: string;
  partnerName: string;
  liquidityHash: string | null;
  /** The price to buy now (0–1). */
  price: number;
  american: string | null;
  /** Taker fee as a fraction of the stake (fee_packet.taker_fee ÷ 100; 0 when absent). */
  takerFee: number;
  /** Maker fee as a fraction of the stake. */
  makerFee: number;
}

/** A side's actionable venue prices (Real: the allowed venues only), cheapest first. */
export function liveQuotes(pos: PositionLike | null | undefined, allowed: ReadonlySet<string> | null): LiveQuote[] {
  const out: LiveQuote[] = [];
  for (const pl of pos?.partner_liquidities ?? []) {
    if (!isActionable(pl) || (allowed && !allowed.has(pl.partner_id))) continue;
    const packet = (pl as { fee_packet?: FeePacketLike | null }).fee_packet;
    const tf = packet?.taker_fee;
    out.push({
      partnerId: pl.partner_id,
      partnerName: pl.partner_name ?? pl.partner_id,
      liquidityHash: pl.liquidity_hash ?? null,
      price: pl.price as number,
      american: pl.american_odds ?? null,
      takerFee: typeof tf === "number" && Number.isFinite(tf) && tf > 0 && tf < 100 ? tf / 100 : 0,
      makerFee: makerFeeRate(packet),
    });
  }
  return out.sort((a, b) => a.price - b.price || a.partnerName.localeCompare(b.partnerName));
}

export interface GetOut {
  long: 0 | 1;
  short: 0 | 1;
  /** The short side's missing contracts. */
  contracts: number;
  /** Where it's bought: the allowed venue with the lowest price after its taker fee. */
  quote: LiveQuote;
  /** The market order (sized at the live price, ceiling 1¢ above it). */
  order: HedgeOrder;
  /** Estimated taker fee (0 in Paper). */
  fee: number;
  /** Payout per outcome once it fills (both about the same: that's the point). */
  payout: [number, number];
  /** The big number: the worse outcome after getting out. */
  locked: number;
}

/**
 * Get out now: buy the short side's missing contracts at the best live allowed venue price with
 * a market order, and lock the result in. `fees`: count each venue's taker fee (Real; Paper
 * pays none). Null when balanced or nothing quotes the short side.
 */
export function getOut(
  book: OppBook,
  live: readonly [PositionLike | null | undefined, PositionLike | null | undefined],
  allowed: ReadonlySet<string> | null,
  opts: { ccy: Ccy; fees: boolean },
): GetOut | null {
  const need = hedgeNeed(book);
  if (!need) return null;
  const quotes = liveQuotes(live[need.short], allowed);
  const allIn = (q: LiveQuote) => q.price * (1 + (opts.fees ? q.takerFee : 0));
  const quote = quotes.reduce<LiveQuote | null>((best, q) => (!best || allIn(q) < allIn(best) - EPS ? q : best), null);
  if (!quote) return null;
  const order = hedgeOrder(need.contracts, quote.price, opts.ccy);
  if (!order) return null;
  const fee = opts.fees ? Math.round(order.amount * quote.takerFee * 100) / 100 : 0;
  const p = hedgePreview(book, need.short, order.amount + fee, order.contracts);
  return { long: need.long, short: need.short, contracts: need.contracts, quote, order, fee, payout: p.payout, locked: p.min };
}

export interface WaitOutcome {
  /** The short side's resting bids. */
  legs: MmLeg[];
  /** Contracts they'd add. */
  contracts: number;
  /** Payout per outcome if they all fill. */
  payout: [number, number];
  min: number;
  max: number;
}

/** "Or wait": the payout per outcome if the short side's resting bids fill. Null with none resting. */
export function waitOutcome(book: OppBook): WaitOutcome | null {
  const need = hedgeNeed(book);
  if (!need) return null;
  const legs = book.sides[need.short].legs.filter((l) => book.resting.includes(l) && (l.price ?? 0) > 0);
  if (legs.length === 0) return null;
  const r = payoutIfFill(book, (_l, side) => side === need.short);
  return { legs, contracts: r.contracts[need.short], payout: r.payout, min: Math.min(...r.payout), max: Math.max(...r.payout) };
}

/** The long side's resting bids (Get out cancels them first: they'd keep adding exposure). */
export function longRestingIds(book: OppBook, long: 0 | 1): string[] {
  return book.sides[long].legs.filter((l) => book.resting.includes(l)).map((l) => l.router_order_id);
}

/** A side's market now vs your best resting bid (Working). */
export interface BidVsMarket {
  col: 0 | 1;
  /** Your highest resting bid on this side, whole cents. */
  bidCents: number;
  /** The cheapest live price to buy this side now (allowed venues). */
  market: LiveQuote | null;
}

const restingOn = (book: OppBook, col: 0 | 1): MmLeg[] => book.sides[col].legs.filter((l) => book.resting.includes(l) && (l.price ?? 0) > 0);

export function bidsVsMarket(book: OppBook, quotes: readonly [LiveQuote[], LiveQuote[]]): BidVsMarket[] {
  const out: BidVsMarket[] = [];
  for (const col of [0, 1] as const) {
    const legs = restingOn(book, col);
    if (legs.length === 0) continue;
    const bidCents = clampCents(Math.max(...legs.map((l) => (l.price ?? 0) * 100)));
    out.push({ col, bidCents, market: quotes[col][0] ?? null });
  }
  return out;
}

/** The market has moved this many cents (or more) away from a resting bid: offer [Move bid]. */
export const MOVED_AWAY_CENTS = 3;

export interface MoveBid {
  col: 0 | 1;
  /** The side's resting bids, cancelled first. */
  cancelIds: string[];
  oldCents: number;
  /** The new default bid: re-planned on the live prices at the pair's original margin. */
  newCents: number;
  /** Where it goes: the old bid's venue when it still quotes, else the highest-priced allowed venue. */
  quote: LiveQuote;
  /** Whole contracts still resting on that side (moved over). */
  contracts: number;
  /** The pair's margin with the moved bid against the other side's resting bid (fraction of $1), null without one. */
  marginAfter: number | null;
}

/**
 * Working: the sides whose market moved away from your resting bid, with the re-quote that
 * brings it back. The new bid is what the builder would compute now (`planBids`) at the margin
 * the two resting bids gave, centred on the live venue prices (consensus doesn't stream).
 */
export function movedAway(
  book: OppBook,
  live: readonly [PositionLike | null | undefined, PositionLike | null | undefined],
  allowed: ReadonlySet<string> | null,
  refs: Record<string, LegRef>,
  opts: { fees: boolean; threshold?: number },
): MoveBid[] {
  const quotes = [liveQuotes(live[0], allowed), liveQuotes(live[1], allowed)] as const;
  const legs = [restingOn(book, 0), restingOn(book, 1)] as const;
  const top = (col: 0 | 1): MmLeg | undefined => [...legs[col]].sort((a, b) => (b.price ?? 0) - (a.price ?? 0))[0];
  // Per side: the old bid's venue if it still quotes, else the highest price (closest to the trading).
  const pick = (col: 0 | 1): LiveQuote | undefined => {
    const l = top(col);
    const venue = l ? refs[l.router_order_id]?.partnerId : undefined;
    return quotes[col].find((q) => q.partnerId === venue) ?? [...quotes[col]].sort((a, b) => b.price - a.price)[0];
  };
  const picks = [pick(0), pick(1)] as const;
  if (!picks[0] || !picks[1]) return [];
  const fair = fairPrices(picks[0].price, picks[1].price);
  if (!fair) return [];
  const fee = (q: LiveQuote) => (opts.fees ? q.makerFee : 0);
  const fees: [number, number] = [fee(picks[0]), fee(picks[1])];
  const old: [number | null, number | null] = [top(0) ? clampCents((top(0)!.price ?? 0) * 100) : null, top(1) ? clampCents((top(1)!.price ?? 0) * 100) : null];
  const margin = old[0] != null && old[1] != null ? Math.max(0.01, pairMargin([old[0], old[1]], fees)) : DEFAULT_MARGIN_PCT / 100;
  const caps = [defaultBidCents(picks[0].price), defaultBidCents(picks[1].price)];
  if (caps[0] == null || caps[1] == null) return [];
  const plan = planBids(fair, margin, [
    { capCents: caps[0], feeRate: fees[0] },
    { capCents: caps[1], feeRate: fees[1] },
  ]);
  const threshold = opts.threshold ?? MOVED_AWAY_CENTS;
  const out: MoveBid[] = [];
  for (const col of [0, 1] as const) {
    const o = old[col];
    if (o == null || plan.cents[col] - o < threshold) continue;
    const contracts = Math.round(legs[col].reduce((n, l) => n + unfilledOf(l) / (l.price as number), 0));
    if (!(contracts > 0)) continue;
    const other = old[col === 0 ? 1 : 0];
    const pair: [number, number] = col === 0 ? [plan.cents[0], other ?? 0] : [other ?? 0, plan.cents[1]];
    out.push({
      col,
      cancelIds: legs[col].map((l) => l.router_order_id),
      oldCents: o,
      newCents: plan.cents[col],
      quote: picks[col]!,
      contracts,
      marginAfter: other != null ? pairMargin(pair, fees) : null,
    });
  }
  return out;
}

/* ============================================================ model + server list */

/** The model every Market making order is filed under (docs: Models & attribution). */
export const MM_MODEL_NAME = "greenbook · Market making";
export const MM_MODEL_DESCRIPTION = "Two-sided limit quotes placed from greenbook's Market making screen.";
/**
 * A FIXED Idempotency-Key for creating it, so two tabs or devices racing to create it get one
 * model. The docs don't say whether the key is scoped per account (FRICTION.md).
 */
export const MM_MODEL_IDEMPOTENCY_KEY = "greenbook-market-making-v1";

/** The subset of a `/auth/models` row read here. */
export interface ModelLike {
  model_id: string;
  model_name: string;
  status: string;
  created_at?: string | null;
}

/**
 * The account's Market making model: the ACTIVE one named exactly {@link MM_MODEL_NAME}, the
 * oldest when there are several (a lost create race), so every device settles on the same one.
 */
export function pickMmModel<M extends ModelLike>(models: readonly M[]): M | null {
  const hits = models.filter((m) => m.model_name === MM_MODEL_NAME && norm(m.status) === "active");
  hits.sort((a, b) => tsOf(a.created_at) - tsOf(b.created_at) || a.model_id.localeCompare(b.model_id));
  return hits[0] ?? null;
}

/** The subset of a `/auth/opportunities` row read here. */
export interface MmOppLike {
  opportunity_id: string;
  contest_id?: string | null;
  model_id?: string | null;
  funding?: { currency?: string | null } | null;
  created_at?: string | null;
  legs?: MmLeg[] | null;
}

/** What paper legs name as their venue: OpenMarkets' practice book (`partner.internal_book`). */
export const PRACTICE_BOOK = "practice book";

/** A leg's venue as the row says it booked: the venue's name, or the practice book for paper. */
export function legVenueName(l: MmLeg): string {
  if (l.partner?.internal_book) return PRACTICE_BOOK;
  return l.partner?.partner_name ?? l.partner_id ?? "—";
}

/**
 * The book whose two sides hold these positions, matched on position hashes (the
 * `liquidity_hash` tail, `positionHashOf`). The book holding the most of them wins; null when
 * none holds any (the line is gone from the feed).
 */
export function matchBook(markets: readonly MmMarket[], hashes: readonly string[]): { market: MmMarket; book: MmBook; ladder: Ladder } | null {
  const want = new Set(hashes);
  if (want.size === 0) return null;
  let best: { market: MmMarket; book: MmBook; ladder: Ladder; hits: number } | null = null;
  for (const market of markets) {
    for (const book of market.books) {
      if (!book.positions.some((p) => want.has(p.position_hash))) continue;
      const ladder = buildLadder(book, market.shape, { mode: "paper", allowed: null, limitVenues: null });
      if (!ladder) continue;
      const hits = ladder.columns.filter((c) => want.has(c.positionHash)).length;
      if (hits > 0 && (!best || hits > best.hits)) best = { market, book, ladder, hits };
    }
  }
  return best ? { market: best.market, book: best.book, ladder: best.ladder } : null;
}

/** "Moneyline", or the market and its line: "Spread · Hawks −3.5" (the label the builder registers). */
export const marketTitle = (market: Pick<MmMarket, "label">, book: Pick<MmBook, "key" | "label">): string =>
  book.key === "main" ? market.label : `${market.label} · ${book.label}`;

/**
 * Rebuild a card's entry from the server's opportunity, for a quote this browser has no entry
 * for (placed on another device). The sides, market and book come from matching the legs'
 * position hashes against the game's quotable books; until those load (or when the line is gone)
 * the sides are the legs' own positions, labelled by their titles. Each leg's venue is the one it
 * booked at: for paper legs that's the practice book (the row doesn't carry the venue it targeted).
 */
export function entryFromOpportunity(opp: MmOppLike, ctx: { game?: string | null; markets?: readonly MmMarket[] | null } = {}): MmEntry {
  const legs = sortLegs((opp.legs ?? []) as MmLeg[]);
  const hashes: string[] = [];
  const titles = new Map<string, string>();
  for (const l of legs) {
    const h = positionHashOf(l);
    if (!h) continue;
    if (!hashes.includes(h)) hashes.push(h);
    if (l.title && !titles.has(h)) titles.set(h, l.title);
  }
  const ccyRaw = (opp.funding?.currency ?? legs.find((l) => l.currency)?.currency ?? "").toUpperCase();
  const ccy: Ccy = ccyRaw === "USD" ? "USD" : "ATLAS";
  const first = legs[0];
  const createdAt = tsOf(opp.created_at) || tsOf(first?.created_at ?? first?.placed_datetime) || 0;
  const refs: Record<string, LegRef> = {};
  for (const l of legs) {
    const h = positionHashOf(l);
    if (h) refs[l.router_order_id] = { positionHash: h, partnerId: l.partner_id ?? l.partner?.partner_id ?? "", partnerName: legVenueName(l) };
  }
  const base = {
    opportunityId: opp.opportunity_id,
    modelId: opp.model_id ?? null,
    remote: true,
    ccy,
    createdAt,
    contestId: opp.contest_id ?? first?.contest_id ?? "",
    game: ctx.game ?? "",
    legs: refs,
  };
  const hit = ctx.markets ? matchBook(ctx.markets, hashes) : null;
  if (hit) {
    return {
      ...base,
      marketKey: hit.market.marketKey,
      bookKey: hit.book.key,
      market: marketTitle(hit.market, hit.book),
      shape: hit.market.shape,
      sides: hit.ladder.columns.map((c) => ({ positionHash: c.positionHash, label: c.label, outcome: c.outcome })) as [MmSide, MmSide],
    };
  }
  // Not matched (yet): the legs' own positions. A quote whose one side never booked has one.
  const side = (i: number): MmSide => {
    const h = hashes[i];
    const t = h ? (titles.get(h) ?? "") : "";
    return { positionHash: h ?? `missing-side-${i}`, label: t || (i === 0 ? "Side A" : "Side B"), outcome: t };
  };
  return {
    ...base,
    marketKey: "",
    bookKey: "",
    market: legs.find((l) => l.title)?.title ?? "",
    shape: "moneyline",
    sides: [side(0), side(1)],
  };
}

/** One quote on the Market making list. */
export interface MmQuote {
  entry: MmEntry;
  /** The server's opportunity when the model's list carries it; absent for a local-only entry. */
  opp?: MmOppLike;
}

/**
 * The list: every opportunity the server files under the Market making model, joined with the
 * local registry, minus what Remove hid here.
 *
 *   server + local   the local entry (labels and the venue each leg targeted), its model from the
 *                    server, plus a ref for any leg placed elsewhere (another device's re-quote)
 *   server only      placed on another device: rebuilt from its legs (`entryFromOpportunity`;
 *                    the card refines it once the game's books load)
 *   local only       a legacy quote (no model, so the server list never has it), or one just
 *                    placed that the list hasn't caught up with: read by id
 *
 * Newest first; the page sorts by status after.
 */
export function mergeQuotes(local: readonly MmEntry[], server: readonly MmOppLike[] | null | undefined, hidden: ReadonlySet<string> = new Set()): MmQuote[] {
  const byId = new Map(local.map((e) => [e.opportunityId, e]));
  const out: MmQuote[] = [];
  const seen = new Set<string>();
  for (const opp of server ?? []) {
    const id = opp.opportunity_id;
    if (seen.has(id) || hidden.has(id)) continue;
    seen.add(id);
    const mine = byId.get(id);
    if (!mine) {
      out.push({ entry: entryFromOpportunity(opp), opp });
      continue;
    }
    const rebuilt = entryFromOpportunity(opp).legs;
    out.push({ entry: { ...mine, modelId: opp.model_id ?? null, legs: { ...rebuilt, ...mine.legs } }, opp });
  }
  for (const e of local) {
    if (seen.has(e.opportunityId) || hidden.has(e.opportunityId)) continue;
    seen.add(e.opportunityId);
    out.push({ entry: e });
  }
  return out.sort((a, b) => b.entry.createdAt - a.entry.createdAt);
}

/* =================================================================== registry */

/**
 * One Market making quote (an opportunity). Written locally when it's placed from this screen
 * (the API doesn't say which venue a paper leg targeted, FRICTION.md), or rebuilt from the
 * server's opportunity (`entryFromOpportunity`) when it was placed on another device.
 */
export interface MmEntry {
  opportunityId: string;
  /**
   * The model its orders are filed under. Null/absent: none (a quote from before greenbook
   * filed quotes under a model). Every later order on it must send this same model, or none
   * when it has none, or the API answers `400 model_mismatch`.
   */
  modelId?: string | null;
  /** Rebuilt from the server's legs (placed on another device): no local entry. */
  remote?: boolean;
  ccy: Ccy;
  createdAt: number;
  contestId: string;
  /** "Hawks @ Heat". */
  game: string;
  marketKey: string;
  bookKey: string;
  /** "Moneyline", "Spread · Hawks −3.5". */
  market: string;
  shape: MarketShape;
  sides: [MmSide, MmSide];
  /** router_order_id → the side and venue that leg targeted. */
  legs: Record<string, LegRef>;
}

export const REGISTRY_KEY = "gb.mm.registry.v1";
/** Oldest entries drop off past this many. */
export const REGISTRY_MAX = 200;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** localStorage, or null where it isn't available (private mode, SSR, blocked). */
export function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

const isStr = (v: unknown): v is string => typeof v === "string" && v.length > 0;

function validSide(v: unknown): v is MmSide {
  const s = v as MmSide;
  return !!s && typeof s === "object" && isStr(s.positionHash) && typeof s.label === "string" && typeof s.outcome === "string";
}

function validEntry(v: unknown): v is MmEntry {
  const e = v as MmEntry;
  return (
    !!e &&
    typeof e === "object" &&
    isStr(e.opportunityId) &&
    (e.ccy === "USD" || e.ccy === "ATLAS") &&
    typeof e.createdAt === "number" &&
    isStr(e.contestId) &&
    Array.isArray(e.sides) &&
    e.sides.length === 2 &&
    e.sides.every(validSide) &&
    !!e.legs &&
    typeof e.legs === "object"
  );
}

/** Every remembered opportunity, newest first. Malformed entries are dropped; never throws. */
export function readRegistry(store: StorageLike | null = defaultStorage()): MmEntry[] {
  try {
    const raw = store?.getItem(REGISTRY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(validEntry)
      .map(({ remote: _remote, ...e }) => ({
        ...e,
        modelId: isStr(e.modelId) ? e.modelId : null,
        game: e.game ?? "",
        market: e.market ?? "",
        marketKey: e.marketKey ?? "",
        bookKey: e.bookKey ?? "",
        shape: e.shape ?? "moneyline",
      }))
      .sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

const listeners = new Set<() => void>();

/** Notified after every registry write in this tab. */
export function subscribeRegistry(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Replace the registry (capped, newest kept). False when storage is unavailable or full. */
export function writeRegistry(entries: MmEntry[], store: StorageLike | null = defaultStorage()): boolean {
  let ok = false;
  try {
    if (store) {
      const kept = [...entries].sort((a, b) => b.createdAt - a.createdAt).slice(0, REGISTRY_MAX);
      store.setItem(REGISTRY_KEY, JSON.stringify(kept));
      ok = true;
    }
  } catch {
    ok = false;
  }
  for (const l of listeners) l();
  return ok;
}

/** Add an opportunity, or merge new legs into one already remembered. */
export function registerOpportunity(entry: MmEntry, store: StorageLike | null = defaultStorage()): MmEntry[] {
  const all = readRegistry(store);
  const i = all.findIndex((e) => e.opportunityId === entry.opportunityId);
  if (i >= 0) all[i] = { ...all[i], legs: { ...all[i].legs, ...entry.legs } };
  else all.unshift(entry);
  writeRegistry(all, store);
  return readRegistry(store);
}

/** Record more legs (a re-quote or a hedge) on a remembered opportunity. No-op when unknown. */
export function addRegistryLegs(opportunityId: string, legs: Record<string, LegRef>, store: StorageLike | null = defaultStorage()): MmEntry[] {
  const all = readRegistry(store);
  const i = all.findIndex((e) => e.opportunityId === opportunityId);
  if (i < 0) return all;
  all[i] = { ...all[i], legs: { ...all[i].legs, ...legs } };
  writeRegistry(all, store);
  return readRegistry(store);
}

/** Drop an opportunity from the list (it stays on the account). */
export function forgetOpportunity(opportunityId: string, store: StorageLike | null = defaultStorage()): MmEntry[] {
  const all = readRegistry(store).filter((e) => e.opportunityId !== opportunityId);
  writeRegistry(all, store);
  return all;
}

/**
 * Opportunities Remove dropped from the list. A server-listed quote can't be deleted (it stays on
 * the account, and the server keeps listing it under the model), so Remove hides it here, per
 * browser, alongside forgetting any local entry.
 */
export const HIDDEN_KEY = "gb.mm.hidden.v1";
/** Oldest hidden ids drop off past this many. */
export const HIDDEN_MAX = 500;

/** The hidden opportunity ids (in the order they were hidden). Never throws. */
export function readHidden(store: StorageLike | null = defaultStorage()): string[] {
  try {
    const raw = store?.getItem(HIDDEN_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter(isStr) : [];
  } catch {
    return [];
  }
}

/** Hide an opportunity from the list. False when storage is unavailable or full. */
export function hideOpportunity(opportunityId: string, store: StorageLike | null = defaultStorage()): boolean {
  let ok = false;
  try {
    if (store) {
      const kept = [...readHidden(store).filter((id) => id !== opportunityId), opportunityId].slice(-HIDDEN_MAX);
      store.setItem(HIDDEN_KEY, JSON.stringify(kept));
      ok = true;
    }
  } catch {
    ok = false;
  }
  for (const l of listeners) l();
  return ok;
}

/* ================================================================ walkthrough */

/** The first-time walkthrough has been seen (or skipped). */
export const TOUR_KEY = "gb.mm.tour.v1";

/** Has this browser seen the walkthrough? Unreadable storage counts as not seen; never throws. */
export function readTourSeen(store: StorageLike | null = defaultStorage()): boolean {
  try {
    return store?.getItem(TOUR_KEY) === "1";
  } catch {
    return false;
  }
}

/** Mark the walkthrough seen (or not, to replay it). False when storage is unavailable. */
export function writeTourSeen(seen: boolean, store: StorageLike | null = defaultStorage()): boolean {
  try {
    if (!store) return false;
    store.setItem(TOUR_KEY, seen ? "1" : "0");
    return true;
  } catch {
    return false;
  }
}

/** The registry (and the hidden set) as a React store: re-read on every write here and on writes from other tabs. */
let registryCache: MmEntry[] | null = null;
let hiddenCache: ReadonlySet<string> | null = null;
function registrySnapshot(): MmEntry[] {
  if (!registryCache) registryCache = readRegistry();
  return registryCache;
}
function hiddenSnapshot(): ReadonlySet<string> {
  if (!hiddenCache) hiddenCache = new Set(readHidden());
  return hiddenCache;
}
function subscribeRegistryStore(cb: () => void): () => void {
  const onWrite = () => {
    registryCache = null;
    hiddenCache = null;
    cb();
  };
  const off = subscribeRegistry(onWrite);
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith("gb.mm.")) onWrite();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    off();
    window.removeEventListener("storage", onStorage);
  };
}
const NO_ENTRIES: MmEntry[] = [];
const NO_HIDDEN: ReadonlySet<string> = new Set();

/** The opportunities Remove hid in this browser, live. */
export function useMmHidden(): ReadonlySet<string> {
  return useSyncExternalStore(subscribeRegistryStore, hiddenSnapshot, () => NO_HIDDEN);
}

/** The Market making registry, live (Market making, Portfolio's plays, the nav badge). */
export function useMmRegistry(): MmEntry[] {
  return useSyncExternalStore(subscribeRegistryStore, registrySnapshot, () => NO_ENTRIES);
}

/**
 * The registry's opportunity ids. Only this browser's quotes: lib/mmModel.ts `useMmQuoteIds`
 * adds the server's (every device's) and is what the pages read.
 */
export function useMmIds(): ReadonlySet<string> {
  const reg = useMmRegistry();
  return useMemo(() => new Set(reg.map((e) => e.opportunityId)), [reg]);
}

/** `?focus=<opportunityId>` opens Market making scrolled to that card. */
export const mmFocusHref = (opportunityId: string): string => `/market-making?${new URLSearchParams({ focus: opportunityId }).toString()}`;

/* ===================================================================== access */

/**
 * May this user open Market making? Always yes for now. A one-time product purchase will gate
 * it later; this hook is the one place that check goes (the route and the nav both read it).
 */
export function useMarketMakingAccess(): boolean {
  return true;
}

/* ================================================================== short labels */

/** A team/player name's last word: "Golden State Warriors" → "Warriors". Short names pass through. */
export function shortName(name: string): string {
  const words = name.trim().split(/\s+/);
  return words.length > 1 ? words[words.length - 1] : name.trim();
}

/** "Minnesota Timberwolves +14.5" → "Timberwolves +14.5"; "Over 220.5" stays. */
export function shortSide(label: string): string {
  const m = /^(.*?)\s+([+\-−]\d[\d.]*|\d[\d.]*)$/.exec(label.trim());
  if (!m) return shortName(label);
  return /^(over|under)$/i.test(m[1]) ? label.trim() : `${shortName(m[1])} ${m[2]}`;
}

/** "Golden State Warriors @ Minnesota Timberwolves" → "Warriors @ Timberwolves". */
export function shortGame(game: string): string {
  const m = /^(.*?)\s+(@|vs\.?|v)\s+(.*)$/i.exec(game.trim());
  return m ? `${shortName(m[1])} ${m[2]} ${shortName(m[3])}` : game;
}

/** The market without the side it was named after: "Spread · Minnesota Timberwolves +14.5" → "Spread". */
export function shortMarket(market: string): string {
  return market.split(" · ")[0];
}
